import React from 'react';
import { Helmet } from 'react-helmet-async';
import PublicNav from 'layouts/PublicNav';
import PublicFooter from 'layouts/PublicFooter';
import useScrollReveal from 'hooks/useScrollReveal';
import './SlackPage.css';

/* Screenshots for the "How it works in Slack" section.
   `src` is null until the real captures land — the renderer falls back to a
   sized placeholder box so the section still reads as intentional. The `alt`
   string is authored NOW and used by both states, so dropping in the image is
   a one-line change and the accessible name never goes missing. */
const screenshots = [
  {
    /* TODO(founder): screenshot 1600x1000 showing a user @mentioning @Zibby in a
       public channel and Zibby replying inside a thread on that message. */
    src: null,
    alt: 'A Slack channel where a team member @mentions @Teamy and Teamy replies in a thread under the message.',
    title: '@mention it in a channel',
    caption: 'Teamy answers in a thread on your message, so the channel stays readable.',
  },
  {
    /* TODO(founder): screenshot 1600x1000 showing the Sentry triage Block Kit
       board posted by Zibby in a channel, with severity-ranked issues. */
    src: null,
    alt: 'A Sentry triage board posted by Teamy in a Slack channel, listing unresolved issues ranked by severity.',
    title: 'Agents post back to your channels',
    caption: 'The Sentry triage agent posts a severity-ranked board to the channel you configured.',
  },
  {
    /* TODO(founder): screenshot 1600x1000 showing a direct-message conversation
       between a user and the Zibby bot. */
    src: null,
    alt: 'A direct message conversation between a team member and the Teamy bot in Slack.',
    title: 'Or just send it a DM',
    caption: 'Direct messages always get a reply, with no @mention needed.',
  },
];

/* Slack Marketplace review item 3: exactly which messages the bot responds to.
   Keep this table literal and exhaustive — it is the published contract. */
const responseRules = [
  { trigger: '@mention in a channel', behaviour: 'Always replies, in a thread' },
  { trigger: 'Direct message', behaviour: 'Always replies' },
  { trigger: 'Thread reply with no @mention', behaviour: 'Replies only if it is already participating in that thread' },
  { trigger: 'Thread reply that @mentions someone else', behaviour: 'Stays silent' },
  { trigger: 'Any other channel message', behaviour: 'Ignored. Teamy never replies to messages that are not addressed to it' },
  { trigger: 'Image or file sent to Teamy', behaviour: 'Read as part of the request' },
];

/* What can cause Zibby to post into Slack without being spoken to first.
   Every entry here is an agent the customer deploys and configures itself. */
const outboundTriggers = [
  {
    name: 'Sentry triage',
    what: 'Polls the Sentry API on a schedule (every 60 minutes by default) for unresolved, unassigned issues, then posts a severity-ranked board to the channel set in SLACK_CHANNEL.',
    options: [
      'ROUTING_PREFER_AUTHOR=1 also DMs the author of the suspect commit.',
      'ROUTING_HIGH_SEVERITY_GROUP=@oncall @-mentions an on-call usergroup on CRITICAL and HIGH issues.',
      'SEVERITY_THRESHOLD (default MEDIUM) suppresses anything below it.',
    ],
  },
  {
    name: 'Code review (GitHub / GitLab)',
    what: 'Triggered by a pull request or merge request webhook. The review itself goes on the PR or MR, with an optional Slack ping to a channel you name.',
    options: [],
  },
  {
    name: 'GitHub AI scout',
    what: 'A daily scheduled digest of trending AI repositories, posted to a channel.',
    options: [],
  },
  {
    name: 'Notify Slack',
    what: 'A building block other agents call to post a Block Kit message to a channel you specify.',
    options: [],
  },
];

const installSteps = [
  <>Create an account or sign in at <a href="/connect">zibby.dev</a>.</>,
  <>Open a project, go to <strong>Project Settings</strong>, and expand the <strong>Chat integrations</strong> card.</>,
  <>On the <strong>Slack</strong> row, click <strong>Connect</strong>. You are sent to Slack&apos;s standard permission screen. Approve it for your workspace.</>,
  <>You are returned to Project Settings with &ldquo;Slack connected to this project.&rdquo;</>,
  <>From that project&apos;s <strong>Marketplace</strong>, deploy the <strong>Teamy Copilot</strong> agent.</>,
  <>On the Copilot&apos;s <strong>Env</strong> tab, add your Claude credential (<code>CLAUDE_CODE_OAUTH_TOKEN</code> or <code>ANTHROPIC_API_KEY</code>). Teamy runs on your own model credential.</>,
  <>In Slack, invite <code>@Teamy</code> to a channel (required for private channels) and @mention it, or send it a direct message.</>,
];

const firstAsks = [
  'what’s the status of my last run?',
  'deploy the code review agent on this project',
  'show me today’s Sentry issues',
  'trigger the nightly digest now',
];

const SlackPage = () => {
  const pageRef = useScrollReveal();

  return (
    <>
      <Helmet>
        <title>Teamy for Slack - AI agents in your workspace</title>
        <meta
          name="description"
          content="Teamy for Slack puts your AI agents in your workspace. @mention the Teamy Copilot or send it a DM to check runs and deploy agents, and let agents post Sentry triage boards, code review pings and daily digests to the channels you choose."
        />
      </Helmet>

      <div className="public-page slack-page fade-in" ref={pageRef}>
        <PublicNav />

        {/* 1. Hero */}
        <section className="slack-hero">
          <div className="slack-hero-inner">
            <div className="slack-eyebrow reveal">SLACK APP</div>
            <h1 className="slack-title reveal reveal-delay-1">Teamy for Slack</h1>
            <p className="slack-lede reveal reveal-delay-2">
              Put your Teamy AI agents where your team already works. @mention the Teamy Copilot
              in a channel or send it a DM to check on runs and deploy agents, and let your
              agents post triage boards, review pings and digests back to the channels you choose.
            </p>
            <div className="slack-hero-actions reveal reveal-delay-3">
              <a href="/connect" className="slack-btn slack-btn--primary">Get Teamy for Slack</a>
              <a href="#install" className="slack-btn slack-btn--secondary">How to install</a>
            </div>
            <p className="slack-hero-note reveal reveal-delay-3">
              Installing Slack is done from inside a Teamy project, so you sign in first.
              The full step-by-step is <a href="#install">further down this page</a>.
            </p>
          </div>
        </section>

        {/* 2. What it is / the problem it solves */}
        <section className="slack-section">
          <div className="slack-section-inner slack-section-inner--narrow">
            <h2 className="slack-section-title reveal">What Teamy for Slack does</h2>
            <div className="slack-prose reveal reveal-delay-1">
              <p>
                Teamy is a platform for building and running AI agents against your own code and
                tools: code review on pull requests, Sentry triage, scheduled digests, and agents
                you compose yourself. The work those agents do happens in Teamy, but the people who
                care about it are in Slack. That split is the problem this app closes.
              </p>
              <p>
                Connecting Slack to a Teamy project does two things. First, it adds a bot user,
                <strong> Teamy</strong>, that your team can talk to: @mention it in a channel or DM it,
                and it answers as your project&apos;s <strong>Teamy Copilot</strong> agent. You can ask it
                what a run did, deploy an agent from the marketplace, or kick off a job, without
                opening the dashboard. Second, it gives your deployed agents a place to report to:
                agents post Block Kit messages into the specific channels you configure, so a Sentry
                triage board or a code review ping lands in front of the right people.
              </p>
              <p>
                Everything is scoped to the project you connected. One Slack workspace can be
                connected to a project, and the agents in that project are the ones the bot can
                see and operate. Teamy runs on the model credential you supply, so your Copilot
                uses your own Anthropic Claude account rather than a shared pool.
              </p>
            </div>
          </div>
        </section>

        {/* 3. How it works in Slack */}
        <section className="slack-section slack-section--alt">
          <div className="slack-section-inner">
            <h2 className="slack-section-title reveal">How it works in Slack</h2>
            <p className="slack-section-sub reveal reveal-delay-1">
              Three places you will see Teamy once it is connected.
            </p>
            <div className="slack-shots">
              {screenshots.map((shot, i) => (
                <figure key={shot.title} className={`slack-shot reveal${i > 0 ? ` reveal-delay-${i}` : ''}`}>
                  <div className="slack-shot-frame">
                    {shot.src ? (
                      <img className="slack-shot-img" src={shot.src} alt={shot.alt} />
                    ) : (
                      <div className="slack-shot-placeholder" role="img" aria-label={shot.alt}>
                        <span className="slack-shot-placeholder-badge">Screenshot</span>
                        <span className="slack-shot-placeholder-text">{shot.alt}</span>
                      </div>
                    )}
                  </div>
                  <figcaption className="slack-shot-caption">
                    <strong>{shot.title}</strong>
                    <span>{shot.caption}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 4. What Zibby responds to */}
        <section className="slack-section">
          <div className="slack-section-inner slack-section-inner--narrow">
            <h2 className="slack-section-title reveal">What Teamy responds to</h2>
            <p className="slack-section-sub reveal reveal-delay-1">
              Teamy only answers messages that are addressed to it. This is the complete list.
            </p>
            <div className="slack-table-wrap reveal reveal-delay-1">
              <table className="slack-table">
                <thead>
                  <tr>
                    <th scope="col">In Slack</th>
                    <th scope="col">Teamy&apos;s behaviour</th>
                  </tr>
                </thead>
                <tbody>
                  {responseRules.map((rule) => (
                    <tr key={rule.trigger}>
                      <th scope="row">{rule.trigger}</th>
                      <td>{rule.behaviour}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="slack-note reveal reveal-delay-2">
              Teamy never joins channels on its own and must be invited to a private channel
              before it can post there.
            </p>
          </div>
        </section>

        {/* 5. What triggers Zibby to post into Slack */}
        <section className="slack-section slack-section--alt">
          <div className="slack-section-inner">
            <h2 className="slack-section-title reveal">What triggers Teamy to post into Slack</h2>
            <p className="slack-section-sub reveal reveal-delay-1">
              Besides answering you, these are the agents that can send a message on their own,
              and what causes each one to send it.
            </p>
            <div className="slack-trigger-cards">
              {outboundTriggers.map((t, i) => (
                <div key={t.name} className={`slack-trigger-card reveal${i > 0 ? ` reveal-delay-${i > 3 ? 3 : i}` : ''}`}>
                  <h3>{t.name}</h3>
                  <p>{t.what}</p>
                  {t.options.length > 0 && (
                    <ul className="slack-trigger-options">
                      {t.options.map((opt) => <li key={opt}>{opt}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            <p className="slack-note reveal">
              Every one of these posts only to a channel the customer configured. Teamy does not
              message anyone by default.
            </p>
          </div>
        </section>

        {/* 6. Install instructions */}
        <section className="slack-section" id="install">
          <div className="slack-section-inner slack-section-inner--narrow">
            <h2 className="slack-section-title reveal">How to install Teamy for Slack</h2>
            <p className="slack-section-sub reveal reveal-delay-1">
              The install starts inside a Teamy project, so there is no one-click button on this
              page. Seven steps, start to finish.
            </p>
            <ol className="slack-steps reveal reveal-delay-1">
              {installSteps.map((step, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i}>{step}</li>
              ))}
            </ol>
            <p className="slack-note reveal reveal-delay-2">
              Only a workspace or account owner can approve the install. A small set of actions
              (creating API tokens, deleting resources, and anything billing-related) are refused
              for anyone who is not the account owner.
            </p>
            <div className="slack-inline-cta reveal reveal-delay-2">
              <a href="/connect" className="slack-btn slack-btn--primary">Get Teamy for Slack</a>
            </div>
          </div>
        </section>

        {/* 7. After you install */}
        <section className="slack-section slack-section--alt">
          <div className="slack-section-inner slack-section-inner--narrow">
            <h2 className="slack-section-title reveal">After you install</h2>
            <div className="slack-prose reveal reveal-delay-1">
              <p>
                A successful install returns you to Project Settings with a confirmation that
                Slack is connected to that project. If you message the bot before you have
                deployed a Copilot, it does not stay silent: it replies with a link to deploy one.
              </p>
            </div>
            <h3 className="slack-subhead reveal reveal-delay-1">First things to try</h3>
            <ul className="slack-asks reveal reveal-delay-2">
              {firstAsks.map((ask) => (
                <li key={ask}><span className="slack-ask-mention">@Teamy</span> {ask}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* 8. AI disclaimer — Slack requires this to be visually clear for LLM apps */}
        <section className="slack-section">
          <div className="slack-section-inner slack-section-inner--narrow">
            <div className="slack-disclaimer reveal">
              <h2 className="slack-disclaimer-title">AI disclaimer</h2>
              <p>
                Teamy is powered by large language models (Anthropic Claude by default, or the
                model credential you supply).
              </p>
              <p>
                LLM output can be inaccurate or incomplete. Summaries, triage severities and code
                suggestions should be reviewed by a human before you act on them.
              </p>
              <p>
                Teamy does not take consequential actions without a human in the loop. Code changes
                are proposed as pull requests for review and are never merged automatically.
              </p>
              <p>
                Slack data is never used to train language models.
              </p>
            </div>
          </div>
        </section>

        {/* 9. Privacy, security and support */}
        <section className="slack-section slack-section--alt">
          <div className="slack-section-inner slack-section-inner--narrow">
            <h2 className="slack-section-title reveal">Privacy, security and support</h2>
            <div className="slack-legal-grid reveal reveal-delay-1">
              <a className="slack-legal-link" href="/privacy">
                <strong>Privacy policy</strong>
                <span>How we handle your data.</span>
              </a>
              <a className="slack-legal-link" href="/security">
                <strong>Security</strong>
                <span>How the platform is secured.</span>
              </a>
              <a className="slack-legal-link" href="/terms">
                <strong>Terms of service</strong>
                <span>The terms you are agreeing to.</span>
              </a>
              <a className="slack-legal-link" href="/contact">
                <strong>Contact support</strong>
                <span>Reach the team through the support form.</span>
              </a>
            </div>
            <p className="slack-note reveal reveal-delay-2">
              You can also email us directly at{' '}
              <a href="mailto:support@teamy.town">support@teamy.town</a>.
            </p>
          </div>
        </section>

        <PublicFooter />
      </div>
    </>
  );
};

export default SlackPage;
